/* eslint-disable jsx-a11y/label-has-associated-control */
import Router from 'next/router';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import Box from 'shopper/components/Box';
import Button from 'shopper/components/Button';
import Input from 'shopper/components/Input';
import Text from 'shopper/components/Text';

import Divider from 'components/Divider';

import { sendEvent } from 'lib/gtag';
import { formatBRLCurrencyAsNumber } from 'lib/number';
import placeholder from 'lib/placeholder';
import { brlCurrencyMask } from 'lib/string';

const onMinInputClick = (gaEventCategory) => {
  sendEvent({ category: gaEventCategory, action: 'filter_type_price_min' });
};

const onMaxInputClick = (gaEventCategory) => {
  sendEvent({ category: gaEventCategory, action: 'filter_type_price_max' });
};

const FilterOffersByPriceRange = ({
  gaEventCategory,
  priceRange,
  onOffersPriceRangeFilterChange,
  ...rest
}) => {
  const { control, getValues, reset, handleSubmit } = useForm({
    defaultValues: { min: '', max: '' },
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    Router.events.on('routeChangeStart', () => reset({ max: '', min: '' }));
  }, []);

  useEffect(() => {
    if (!priceRange.min && !priceRange.max) {
      reset({ max: '', min: '' });
    }
  }, [priceRange.min, priceRange.max]);

  const onPriceRangeValidate = (value) => {
    const [min, max] = [value, getValues('max')].map((strPrice) =>
      formatBRLCurrencyAsNumber(strPrice)
    );

    return (
      !max ||
      min === 0 ||
      max === 0 ||
      min < max ||
      'O preço mínimo deve ser menor que o máximo'
    );
  };

  const onFiltersPriceSubmit = (newPriceRange) => {
    sendEvent({
      category: gaEventCategory,
      action: 'to_apply_filter_price',
    });

    onOffersPriceRangeFilterChange(newPriceRange);
  };

  return (
    <Box
      as="form"
      className="flex-col"
      onSubmit={handleSubmit(onFiltersPriceSubmit)}
      {...rest}
    >
      <div className="py-4">
        <Text className="mb-4" bold>
          Preço
        </Text>
        <div className="relative flex w-full">
          <Controller
            control={control}
            name="min"
            render={({
              field: { value, onChange, ...fieldProps },
              fieldState: { error },
            }) => (
              <Input
                error={!!error}
                label={placeholder('inputs.labels.min')}
                maxLength={11}
                message={error?.message}
                placeholder={placeholder('inputs.placeholders.min')}
                value={brlCurrencyMask(value?.toString())}
                full
                onChange={({ target }) =>
                  onChange(brlCurrencyMask(target.value))
                }
                onClick={() => onMinInputClick(gaEventCategory)}
                {...fieldProps}
              />
            )}
            rules={{ validate: onPriceRangeValidate }}
          />
          <Divider className="relative bottom-[-60px] mx-2 w-2" />
          <Controller
            control={control}
            name="max"
            render={({ field: { value, onChange, ...fieldProps } }) => (
              <Input
                className="w-full"
                label={placeholder('inputs.labels.max')}
                maxLength={11}
                placeholder={placeholder('inputs.placeholders.max')}
                value={brlCurrencyMask(value?.toString())}
                full
                onChange={({ target }) =>
                  onChange(brlCurrencyMask(target.value))
                }
                onClick={() => onMaxInputClick(gaEventCategory)}
                {...fieldProps}
              />
            )}
          />
        </div>
        <Button className="mt-4 w-full" htmlType="submit" size="size2">
          {placeholder('actions.applyFilter')}
        </Button>
      </div>
    </Box>
  );
};

FilterOffersByPriceRange.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  priceRange: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
  }).isRequired,
  onOffersPriceRangeFilterChange: PropTypes.func.isRequired,
};

export default FilterOffersByPriceRange;
